/**
 * Main configuration file for republic-specific settings.
 * This file serves as a barrel export, assembling configurations from section-based files.
 *
 * All existing imports will continue to work without changes.
 */

import type { Repub } from '@cx-modules/constants';

import { REPUB_CONFIG as PLUS_REPUB_CONFIG } from './product-plus/plus-tools-repub-config';
import { FAVICON_CONFIGS } from './favicon-config';
import { TRACKING_CONFIGS } from './tracking-config';
import type { RepubConfig, SupportedRepubName } from './types';

// Re-export all types
export type { RepubConfig, SupportedRepubName } from './types';

// Re-export OddscheckerPlus types, configs, and helpers
export { type AuthProvider, authProviders } from './auth-provider';
export { BETTING_SPLITS_CONFIGS } from './product-plus/betting-splits-config';
export { CATEGORY_ORDERING_CONFIGS } from './product-plus/category-ordering-config';
export { getPlusLinks } from './product-plus/get-plus-links';
export { PLUS_HOME_PAGE_PATHS } from './product-plus/misc-config';
export { REPUB_CONFIG as PLUS_REPUB_CONFIG } from './product-plus/plus-tools-repub-config';
export { SHOW_AGREE_TERMS_AS_CHECKBOX_CONFIG, SIGN_IN_IMMEDIATELY_AFTER_SIGNUP_CONFIG } from './non-plus-auth-config';
export { type NonPlusAuthUrlsConfig, NON_PLUS_AUTH_URLS_CONFIG } from './non-plus-auth-urls-config';
export { SHOW_MARKETING_CONSENT_CONFIG } from './marketing-consent-config';
export { STRIPE_PRODUCT_IDS, type ProductId } from './product-plus/stripe-constants';
export { getSubscriptionProductConfig, SUBSCRIPTION_CONFIGS } from './product-plus/subscription-config';
export type {
  MetaPixelProductConfig,
  PriceCardDisplayConfig,
  SubscriptionProductConfig,
} from './product-plus/subscription-config';
export { TRENDS_CONFIGS } from './product-plus/trends-config';
export type { PlusToolName, RepubConfig as PlusRepubConfig } from './product-plus/types';
export { VALUE_BETS_CONFIGS } from './product-plus/value-bets-config';

/**
 * Main configuration object mapping republic names to their configurations
 * Assembled from individual section configs
 *
 * Note: Typed as Partial<Record<Repub, RepubConfig>> to allow indexing with any Repub value,
 * but only contains configurations for SupportedRepubName values.
 */
export const REPUB_CONFIG: Partial<Record<Repub, RepubConfig>> & Record<SupportedRepubName, RepubConfig> = {
  WS: {
    favicon: FAVICON_CONFIGS.WS,
    plus: PLUS_REPUB_CONFIG.WS,
    tracking: TRACKING_CONFIGS.WS,
  },
  OC: {
    favicon: FAVICON_CONFIGS.OC,
    plus: PLUS_REPUB_CONFIG.OC,
    tracking: TRACKING_CONFIGS.OC,
  },
  US: {
    favicon: FAVICON_CONFIGS.US,
    plus: PLUS_REPUB_CONFIG.US,
    tracking: TRACKING_CONFIGS.US,
  },
  GZ: {
    favicon: FAVICON_CONFIGS.GZ,
    plus: PLUS_REPUB_CONFIG.GZ,
    tracking: TRACKING_CONFIGS.GZ,
  },
};

export function isSupportedRepub(repub: string): repub is SupportedRepubName {
  return Object.keys(REPUB_CONFIG).includes(repub);
}
